/**
 * Share Result Tracker
 * 
 * Tracks user actions for generating share text when pressing the result modal "deel" button.
 * Each action is stored as a string with an emoji and clicksLeft value.
 * 
 * Actions tracked:
 * - 🟥 {clicksLeft} - User clicks cell, not letter (empty cell)
 * - 🟦 {clicksLeft} - User clicks cell, letter revealed
 * - 🟩 {clicksLeft} - User clicks cell, letter revealed + entire word gets revealed
 * - ❌ {clicksLeft} - User guesses word, incorrect
 * - ✅ {clicksLeft} - User guesses word, correct
 * - 🟩 {clicksLeft} - Rare case: if by luck a second word is revealed
 */

const STORAGE_KEY_PREFIX = 'shareResultActions_';

/**
 * Get the storage key for a specific game
 */
function getStorageKey(gameIdNumber) {
  return `${STORAGE_KEY_PREFIX}${gameIdNumber}`;
}

/**
 * Save share result actions to localStorage
 */
export function saveShareResultActions(gameIdNumber, actions) {
  if (gameIdNumber === undefined || gameIdNumber === null) {
    console.warn('[ShareResult] Cannot save: gameIdNumber is invalid');
    return;
  }

  try {
    const key = getStorageKey(gameIdNumber);
    const data = JSON.stringify(actions);
    localStorage.setItem(key, data);
  } catch (error) {
    console.warn('[ShareResult] Failed to save actions to localStorage:', error);
  }
}

/**
 * Load share result actions from localStorage
 */
export function loadShareResultActions(gameIdNumber) {
  if (gameIdNumber === undefined || gameIdNumber === null) {
    return [];
  }

  try {
    const key = getStorageKey(gameIdNumber);
    const stored = localStorage.getItem(key);
    if (stored) {
      const actions = JSON.parse(stored);
      if (Array.isArray(actions)) {
        return actions;
      }
    }
  } catch (error) {
    console.warn('[ShareResult] Failed to load actions from localStorage:', error);
  }

  return [];
}

/**
 * Clear share result actions for a specific game
 */
export function clearShareResultActions(gameIdNumber) {
  if (gameIdNumber === undefined || gameIdNumber === null) {
    return;
  }

  try {
    const key = getStorageKey(gameIdNumber);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('[ShareResult] Failed to clear actions from localStorage:', error);
  }
}

/**
 * Add an action to the share result array
 * @param {number} gameIdNumber - The game ID number
 * @param {number} clicksLeft - Current number of clicks remaining (not stored, only for logging)
 * @param {string} actionType - Type of action: 'click-empty', 'click-letter', 'click-word', 'guess-wrong', 'guess-correct', 'word-revealed'
 * @returns {string[]} - Updated actions array
 */
export function addShareResultAction(gameIdNumber, clicksLeft, actionType) {
  if (gameIdNumber === undefined || gameIdNumber === null) {
    console.warn('[ShareResult] Cannot add action: gameIdNumber is invalid');
    return [];
  }

  const actions = loadShareResultActions(gameIdNumber);
  let emoji = '';
  
  switch (actionType) {
    case 'click-empty':
      emoji = '🟥';
      break;
    case 'click-letter':
      emoji = '🟦';
      break;
    case 'click-word':
    case 'word-revealed':
      emoji = '🟩';
      break;
    case 'guess-wrong':
      emoji = '❌';
      break;
    case 'guess-correct':
      emoji = '✅';
      break;
    default:
      console.warn(`[ShareResult] Unknown action type: ${actionType}`);
      return actions;
  }

  // Store only the emoji (no clicksLeft)
  const updatedActions = [...actions, emoji];
  
  saveShareResultActions(gameIdNumber, updatedActions);
  
  return updatedActions;
}

/**
 * Initialize share result tracking for a new game
 * Clears any existing actions and starts fresh
 */
export function initShareResultTracking(gameIdNumber) {
  if (gameIdNumber === undefined || gameIdNumber === null) {
    return;
  }

  clearShareResultActions(gameIdNumber);
}

/**
 * Get the current share result actions array
 */
export function getShareResultActions(gameIdNumber) {
  return loadShareResultActions(gameIdNumber);
}
